import { DecimalPipe, NgClass } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, EventEmitter, OnInit, Output, ViewChild, inject } from '@angular/core';
import { FormsModule, NgForm } from '@angular/forms';
import { BOOKING_PAGE_VIEWS, GTM_EVENTS, MEETING_TYPE_ID, WIDGET_TEMPLATES_CONST } from '@constants/app.constants';
import { TEMPLATE_CONSTANT } from '@constants/template.constants';
import { environment } from '@environments/environment';
import { StoreDbModel } from '@models/store-db.model';
import { CurrentViewData } from '@models/widget-conf.model';
import { WidgetTemplateModel } from '@models/widget-template.model';
import { NgSelectModule } from '@ng-select/ng-select';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { AddressFormatPipe } from '@pipes/address-format.pipe';
import { CalioMeetingTemplatePipe } from '@pipes/calio-meeting-template.pipe';
import { CalioSafePipe } from '@pipes/calio-safe.pipe';
import { CalioTranslationPipe } from '@pipes/calio-translation.pipe';
import { ResolvePlaceholderPipe } from '@pipes/resolve-placeholder.pipe';
import { BookingService } from '@services/feature/booking.service';
import { WidgetService } from '@services/feature/widget.service';
import { GoogleAnalyticsService } from '@services/utils/google-analytics.service';
import { LoggerService } from '@services/utils/logger.service';
import { SwRouteService } from '@services/utils/sw-route.service';
import { resolveCustomerEnteredPostcode } from '@services/utils/store-postcode.util';
import { workerServesStore } from '@services/utils/store-scope.util';
import { ButtonComponent } from '@ui-lib/buttons/button/button.component';
import { InfoCardComponent } from '@ui-lib/cards/info-card/info-card.component';
import { CswTextComponent } from '@ui-lib/typography/csw-text/csw-text.component';
import { of } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, finalize, switchMap } from 'rxjs/operators';

const components = [CswTextComponent, InfoCardComponent, ButtonComponent];
const pipes = [DecimalPipe, CalioSafePipe, CalioTranslationPipe, CalioMeetingTemplatePipe, ResolvePlaceholderPipe, AddressFormatPipe];
const modules = [FormsModule, NgSelectModule, TranslateModule];

@Component({
  selector: 'app-store-selection',
  templateUrl: './store-selection.component.html',
  styleUrls: ['./store-selection.component.scss'],
  imports: [NgClass, ...pipes, components, modules],
  providers: [CalioTranslationPipe]
})
export class StoreSelectionComponent implements OnInit {

  @Output() gotoPage = new EventEmitter<CurrentViewData>();
  @Output() previousPageEvent = new EventEmitter<CurrentViewData>();

  private googleAnalyticsService = inject(GoogleAnalyticsService);
  private swRouteService = inject(SwRouteService);
  private translate = inject(TranslateService);
  public bookingService = inject(BookingService);
  public widgetService = inject(WidgetService);

  protected readonly baseUrl = environment.imageHostName;
  protected readonly templateConstant = TEMPLATE_CONSTANT;

  selectedStoreId: number;
  selectedStore: StoreDbModel;
  stores: StoreDbModel[];
  lang: string;
  storeLabelTemplate: WidgetTemplateModel;
  anyStoreLabelTemplate: WidgetTemplateModel;
  slotGhostElements = new Array<number>(4);
  showGhostElement = true;
  showPreviousButton = false;
  searchStores = new EventEmitter<string>();
  isStoreLoading = false;
  selectedStoreLeadGeneratorPostcode: string;
  // True only when the search term above came from the customer typing it (or from a restored state
  // whose value the customer typed). A value restored from a booking question's store zip is not.
  private postcodeTypedByCustomer = false;
  // The search term and result list as they were when the store was picked. The dropdown clears its
  // search after a pick and a later search can empty `stores`, so next() must not read the live list.
  private pickedSearchTerm: string | null = null;
  private pickedSearchResults: StoreDbModel[] = [];
  isGTMEventSent: boolean;
  /** Read FRESH, never cached: this mounts once, so an ngOnInit value pins to stale answers. */
  get supportedAppointmentServiceIds(): number[] {
    return this.bookingService.getSupportedAppointmentServiceIdsFromLocalStorage(
      this.bookingService.partnerData?.booking_name
    );
  }
  showStoreSelectionRequiredError = false;
  enableStorePostcodeFeature: number;
  automaticStoreZipSearch: number;

  @ViewChild('storeForm') storeForm: NgForm;

  constructor() {
    this.searchStores.pipe(
      distinctUntilChanged(),
      debounceTime(300),
      catchError(() => { return of([]) }),
      switchMap((term: string) => {
        this.isStoreLoading = true;
        if (term?.trim().length >= 4) {
          this.selectedStoreLeadGeneratorPostcode = term;
          this.postcodeTypedByCustomer = true;
          this.stores = [];
          return this.bookingService.getStoresByPostCode(term);
        } else {
          return of([]);
        }
      })
    ).subscribe({
      next: stores => {
        this.stores = stores;
        if (this.stores?.length === 1) {
          this.selectedStoreId = this.stores[0].id;
          setTimeout(() => {
            this.onStoreChange(this.selectedStoreId);
            this.next();
          }, 500);
        }

        this.isStoreLoading = false;
      },
      error: (error: HttpErrorResponse) => {
        this.isStoreLoading = false;
        LoggerService.error(error);
      }
    });

    this.translate.onLangChange.subscribe(language => this.lang = language.lang);
  }

  ngOnInit(): void {
    this.isGTMEventSent = false;
    this.lang = this.translate.getFallbackLang();

    this.enableStorePostcodeFeature = this.widgetService?.widgetConf?.partner?.enable_store_postcode_feature;
    this.automaticStoreZipSearch = this.widgetService?.widgetConf?.partner?.automatic_store_zip_search;

    // For Helsana, if meeting type is customer and show_store_selection_for_customer_meeting_type context is true, then enable store postcode feature is enabled
    if (this.bookingService?.selectedBookingData?.currentSelectedAppointmentState?.selectedMeetingTypeId === MEETING_TYPE_ID.CUSTOMER &&
      this.widgetService.widgetConf?.context?.show_store_selection_for_customer_meeting_type) {
      this.enableStorePostcodeFeature = 1;
    }

    this.bookingService.loadAppointmentStateFromLocalStorage();

    if (
      // [hidden_booking_steps] A hidden STORE forces context.store_id, so the postcode-search UI is
      // never shown to the user. Fetch the full store list directly (regardless of the postcode /
      // automatic-zip setting) so fetchAvailableStores() can resolve and skip the forced store.
      this.bookingService.isStepHidden(BOOKING_PAGE_VIEWS.STORE_VIEW)
    ) {
      this.fetchAvailableStores();
    } else if (
      this.enableStorePostcodeFeature === 1 ||
      this.automaticStoreZipSearch === 1
    ) {
      if (this.bookingService?.selectedBookingData?.currentSelectedAppointmentState?.selectedStoreLeadGeneratorPostcode) {
        this.selectedStoreLeadGeneratorPostcode = this.bookingService?.selectedBookingData?.currentSelectedAppointmentState?.selectedStoreLeadGeneratorPostcode;
        this.postcodeTypedByCustomer =
          this.bookingService.selectedBookingData.currentSelectedAppointmentState.storeLeadGeneratorPostcodeEnteredByCustomer === true;
        this.getStoresByPostCode(this.selectedStoreLeadGeneratorPostcode);
      } else {
        // Commented code for helsana to hide default store list when no postcode is entered
        // if (this.widgetService.isDefaultStoreIdSelected()) {
        //   this.fetchAvailableStores();
        // } else {
        //   this.showGhostElement = false;
        // }
        this.showGhostElement = false;
      }
    } else {
      this.fetchAvailableStores();
    }

    this.setupTemplates();
    this.setupPreviousButton();
  }

  getStoresByPostCode(postcode: string): void {
    this.bookingService.getStoresByPostCode(postcode)
      .pipe(
        finalize(() => this.showGhostElement = false)
      )
      .subscribe({
        next: stores => {
          this.showGhostElement = false;
          if (stores?.length) {
            this.stores = stores;
            if (this.bookingService?.selectedBookingData?.currentSelectedAppointmentState?.selectedStoreId) {
              this.selectedStore = this.stores.find(store =>
                (store.id === Number(this.bookingService?.selectedBookingData?.currentSelectedAppointmentState?.selectedStoreId))
              );
              this.selectedStoreId = this.bookingService.selectedBookingData.currentSelectedAppointmentState.selectedStoreId;
              this.bookingService.selectedBookingData.currentSelectedAppointmentState.selectedStore = this.selectedStore;
              // The restored pick: snapshot it with the restored term, as onStoreChange() does for a new one.
              this.snapshotPick();
            }
          } else {
            this.stores = [];
          }
        }
      });
  }

  setupPreviousButton(): void {
    this.bookingService.isBeforeQuestionsMode() && (this.showPreviousButton = true);
    this.bookingService.isMeetingTypeStepInFlow() && (this.showPreviousButton = true);
  }

  // Fetch available stores
  fetchAvailableStores(): void {
    const appointmentServiceIds = (this.supportedAppointmentServiceIds.length ? this.supportedAppointmentServiceIds : null);
    this.bookingService.getStoresWithWorkers(appointmentServiceIds).subscribe({
      next: stores => {
        this.showGhostElement = false;
        if (stores) {
          if (!this.widgetService.widgetConf.context.is_internal) {
            this.stores = stores.filter(store => store.has_only_internal_appointment_service === 0);
          } else {
            this.stores = stores;
          }

          // Check if worker is preselected through worker preselector field then all the other stores should be disabled
          if (
            this.bookingService.selectedBookingData.currentSelectedAppointmentState.storeAndWorkerPreSelectedThroughWorkerSelectField &&
            this.bookingService.selectedBookingData.currentSelectedAppointmentState.selectedWorker
          ) {
            this.stores.map(store => {
              store.disabled = !workerServesStore(this.bookingService.selectedBookingData.currentSelectedAppointmentState.selectedWorker, store.id);
            });
          }

          // Check if a store was preselected through a store-select booking question — all
          // other stores should be disabled (mirrors the worker-preselector block above).
          if (
            this.bookingService.selectedBookingData.currentSelectedAppointmentState.storePreSelectedThroughStoreSelectField &&
            this.bookingService.selectedBookingData.currentSelectedAppointmentState.selectedStore
          ) {
            this.stores.map(store => {
              store.disabled = !(this.bookingService.selectedBookingData.currentSelectedAppointmentState.selectedStoreId === store.id);
            });
          }

          // [hidden_booking_steps] STORE hidden → force the context store_id and skip in the travel
          // direction (forward on entry, backward when returning through the chain). If the store_id
          // can't resolve to a real store, fall back to rendering the step normally (never a dead-end).
          if (
            this.bookingService.isStepHidden(BOOKING_PAGE_VIEWS.STORE_VIEW) &&
            // If this step already failed to resolve once, it is being rendered normally — treat it
            // as a real reachable step (don't force-skip it again in either direction).
            !this.bookingService.hiddenStepResolutionFailed.has(BOOKING_PAGE_VIEWS.STORE_VIEW)
          ) {
            if (this.bookingService.isNavigatingBackTo(BOOKING_PAGE_VIEWS.STORE_VIEW)) {
              this.previous();
              return;
            }
            const context = this.widgetService.widgetConf?.context;
            // Respect the disabled guard set above: when a worker was pre-selected through the
            // worker-select field, every store except that worker's own is disabled. A forced
            // store_id that points at a disabled (mismatched) store must NOT silently override
            // the customer's worker choice — treat it as unresolved so the step renders normally.
            const forcedStore = this.stores.find(store => store.id === Number(context?.store_id) && !store.disabled);
            if (forcedStore) {
              this.selectedStoreId = forcedStore.id;
              // onStoreChange already advances to SERVICES when one-page / auto-step-forward is on;
              // otherwise advance explicitly. Never do both (would double-navigate).
              this.onStoreChange(this.selectedStoreId, false);
              if (!(context?.enable_one_page_ui || context?.auto_step_forward_on_select)) {
                this.next(false);
              }
            } else {
              this.bookingService.markHiddenStepUnresolved(BOOKING_PAGE_VIEWS.STORE_VIEW);
            }
            return;
          }

          // Check if user is navigating back from a later step.
          // In that case, don't auto-navigate — let them stay to change their selection.
          const isNavigatingBack = (
            this.bookingService.lastView === BOOKING_PAGE_VIEWS.SERVICES_VIEW
          );

          if (this.bookingService?.selectedBookingData?.currentSelectedAppointmentState?.selectedStoreId) {
            this.selectedStore = this.stores.find(store => store.id === Number(this.bookingService?.selectedBookingData?.currentSelectedAppointmentState?.selectedStoreId));
            if (this.selectedStore) {
              this.selectedStoreId = this.bookingService.selectedBookingData.currentSelectedAppointmentState.selectedStoreId;
              this.bookingService.selectedBookingData.currentSelectedAppointmentState.selectedStore = this.selectedStore;
            }

            if (isNavigatingBack) {
              return;
            }

            if (this.widgetService.isDefaultStoreIdSelected()) {
              if (this.swRouteService.isAutoSkipStepsEnabled()) {
                this.onStoreChange(this.selectedStoreId, false);
                this.next(false);
              }
            } else {
              if (this.selectedStoreId && this.selectedStore && this.swRouteService.isAutoSkipStepsEnabled()) {
                this.onStoreChange(this.selectedStoreId, false);
                this.next(false);
              }
            }
          } else {
            if (this.stores?.length === 1 && this.swRouteService.isAutoSkipStepsEnabled()) {
              this.selectedStoreId = this.stores[0].id;
              this.selectedStore = this.stores[0];
              if (!isNavigatingBack) {
                this.next(false);
              }
            }
          }
        } else {
          this.showGhostElement = false;
          this.stores = [];
          // [hidden_booking_steps] Empty/null store list for a hidden STORE step → un-hide it so the
          // user isn't left on an invisible display:none section (never a blank dead-end).
          if (this.bookingService.isStepHidden(BOOKING_PAGE_VIEWS.STORE_VIEW)) {
            this.bookingService.markHiddenStepUnresolved(BOOKING_PAGE_VIEWS.STORE_VIEW);
          }
        }
      },
      error: (err: HttpErrorResponse) => {
        LoggerService.error(err);
        this.showGhostElement = false;
        // [hidden_booking_steps] A transport failure fetching stores must NOT leave a hidden STORE
        // step invisibly stuck — un-hide it so the error/empty state is visible and recoverable.
        if (this.bookingService.isStepHidden(BOOKING_PAGE_VIEWS.STORE_VIEW)) {
          this.bookingService.markHiddenStepUnresolved(BOOKING_PAGE_VIEWS.STORE_VIEW);
        }
      }
    });
  }

  /**
   * Whether an advisor chosen through a worker-select booking question must survive this store
   * pick. Same pair of conditions the store-disabling guard in the stores handler already uses:
   * the flag says the customer named the advisor themselves, and workerServesStore() says the
   * advisor actually covers the store - so a genuine move to a store they do not serve still
   * clears them through the untouched branches below.
   */
  private keepsPreSelectedAdvisor(storeId: number): boolean {
    const state = this.bookingService?.selectedBookingData?.currentSelectedAppointmentState;
    if (
      !state?.storeAndWorkerPreSelectedThroughWorkerSelectField ||
      !state?.selectedWorker ||
      !workerServesStore(state.selectedWorker, Number(storeId))
    ) {
      return false;
    }

    // A store carrying SEVERAL default_worker_id values is not expressing a default - that list is
    // a per-store whitelist, and findWorkers() in the workers step uses it to REPLACE the worker
    // list outright. Keeping an advisor who is not on it would let the customer book someone the
    // store's configuration excludes, so defer to the whitelist in that case. A single default is
    // a genuine fallback for "nobody chose", and the customer did choose, so the advisor wins.
    const whitelist = this.stores.find(store => store.id === Number(storeId))?.default_worker_id;
    if (whitelist?.length > 1) {
      return whitelist.includes(state.selectedWorkerId);
    }

    return true;
  }

  /** Remembers the search the store was picked from; next() resolves the customer postcode from it. */
  private snapshotPick(): void {
    this.pickedSearchTerm = this.postcodeTypedByCustomer ? (this.selectedStoreLeadGeneratorPostcode ?? null) : null;
    this.pickedSearchResults = [...(this.stores ?? [])];
  }

  onStoreChange(selectedStoreId: number, isUserInteracted = true): void {
    this.snapshotPick();

    // Reset error
    this.showStoreSelectionRequiredError = false;

    // trigger start event on first user interaction happen and
    // store selection step is configured to first step
    if (isUserInteracted) {
      this.googleAnalyticsService.triggerApoointmentStartEvent();

      if (!this.isGTMEventSent) {
        this.googleAnalyticsService.emitAppointmentBookingEvent(GTM_EVENTS.appointment_booking_step_stores_start);
        this.isGTMEventSent = true;
      }
    }

    this.selectedStoreId = selectedStoreId;
    this.bookingService.selectedBookingData.currentSelectedAppointmentState.selectedStoreId = selectedStoreId;

    if (this.selectedStoreId !== 0) {
      this.selectedStore = this.stores.find(store => store.id === this.selectedStoreId);
      // An advisor the customer named through a worker-select booking question is not this step's
      // to overwrite. This method also runs PROGRAMMATICALLY, re-selecting the store the customer
      // already answered so the step can auto-skip (the `onStoreChange(this.selectedStoreId,
      // false)` calls above) - and every branch below writes selectedWorkerId. That is how a
      // named advisor was being dropped on the way past a step the customer never even saw, so
      // the slot query went out as `worker_id: 0` and the booking silently rebound to whoever
      // else serves the service.
      // Only when the advisor genuinely serves this store; a real store CHANGE to one they do not
      // serve still clears them, further down, exactly as before.
      if (this.keepsPreSelectedAdvisor(this.selectedStoreId)) {
        // Keep a multi-worker whitelist rather than discarding it: keepsPreSelectedAdvisor() only
        // returns true when the advisor is ON that list, so preserving it changes nothing for
        // them - but it keeps findWorkers() narrowing to the store's configured set if the worker
        // step is ever rendered instead of auto-skipped.
        const whitelist = this.stores.find(store => store.id === Number(this.selectedStoreId))?.default_worker_id;
        this.bookingService.selectedBookingData.currentSelectedAppointmentState.storeDefaultWorkerIds =
          whitelist?.length > 1 ? whitelist : [];
      } else if (this.selectedStore?.default_worker_id?.length) {
        if (this.selectedStore.default_worker_id?.length === 1) {
          this.bookingService.selectedBookingData.currentSelectedAppointmentState.selectedWorkerId = this.selectedStore.default_worker_id[0];
          this.bookingService.selectedBookingData.currentSelectedAppointmentState.selectedWorker = this.selectedStore.default_worker[0];
          this.bookingService.selectedBookingData.currentSelectedAppointmentState.storeDefaultWorkerIds = [];
        } else {
          this.bookingService.selectedBookingData.currentSelectedAppointmentState.selectedWorkerId = null;
          this.bookingService.selectedBookingData.currentSelectedAppointmentState.selectedWorker = null;
          this.bookingService.selectedBookingData.currentSelectedAppointmentState.storeDefaultWorkerIds = this.selectedStore.default_worker_id;
        }
      } else {
        this.bookingService.selectedBookingData.currentSelectedAppointmentState.selectedWorkerId = null;
        this.bookingService.selectedBookingData.currentSelectedAppointmentState.selectedWorker = null;
        this.bookingService.selectedBookingData.currentSelectedAppointmentState.storeDefaultWorkerIds = [];
      }
    } else {
      this.selectedStore = new StoreDbModel();
      this.selectedStore.id = 0;
    }

    if (
      this.widgetService.widgetConf?.context?.enable_one_page_ui ||
      this.widgetService.widgetConf?.context?.auto_step_forward_on_select
    ) {
      // Proceeds to the next step if the one-page UI is enabled.
      // Or if auto step forward on select is enabled.
      this.next();
    }
  }

  previous(): void {
    this.isGTMEventSent = false;
    if (
      this.bookingService.isMeetingTypeStepInFlow() &&
      (
        this.bookingService?.selectedBookingData?.currentSelectedAppointmentState?.selectedMeetingTypeId === MEETING_TYPE_ID.STORE ||
        (
          this.bookingService?.selectedBookingData?.currentSelectedAppointmentState?.selectedMeetingTypeId === MEETING_TYPE_ID.CUSTOMER &&
          this.widgetService.widgetConf?.context?.show_store_selection_for_customer_meeting_type
        )
      )
    ) {
      this.previousPageEvent.emit({ view: BOOKING_PAGE_VIEWS.MEETING_TYPE_VIEW, isUserInteracted: true });
    } else {
      this.previousPageEvent.emit({ view: BOOKING_PAGE_VIEWS.BOOKING_QUESTIONS_VIEW_BEFORE, isUserInteracted: true });
    }
  }

  next(isUserInteracted = true): void {
    this.storeForm?.control?.markAllAsTouched();
    if (this.storeForm?.invalid) return;

    // Meeting type is is not selected and clicked on next then show error.
    if (!this.selectedStoreId && this.selectedStoreId !== 0) {
      this.showStoreSelectionRequiredError = true;
      return;
    }


    // trigger start event on first user interaction happen and
    // store selection step is configured to first step
    if (isUserInteracted) {
      this.googleAnalyticsService.emitAppointmentBookingEvent(GTM_EVENTS.appointment_booking_step_stores_end);
    }

    if (
      this.enableStorePostcodeFeature === 1 ||
      this.automaticStoreZipSearch === 1
    ) {
      const state = this.bookingService.selectedBookingData.currentSelectedAppointmentState;
      // Only the search the store was picked from counts: a term typed after the pick belongs to a
      // search the customer did not pick from.
      const termUnchangedSincePick = this.pickedSearchTerm !== null
        && this.pickedSearchTerm === (this.selectedStoreLeadGeneratorPostcode ?? null);
      const customerPostcode = termUnchangedSincePick
        ? resolveCustomerEnteredPostcode(this.pickedSearchTerm, this.pickedSearchResults, this.selectedStoreId)
        : null;
      // Without a customer postcode the raw term is kept (flag false) so the back-navigation
      // re-search still runs with whatever was typed, e.g. a city name.
      state.selectedStoreLeadGeneratorPostcode = customerPostcode ?? this.selectedStoreLeadGeneratorPostcode;
      state.storeLeadGeneratorPostcodeEnteredByCustomer = !!customerPostcode;
    }

    // Reset current appointment state when store is changed
    if (this.selectedStore?.id !== this.bookingService.selectedBookingData.currentSelectedAppointmentState.selectedStoreId) {
      delete this.bookingService.selectedBookingData.currentSelectedAppointmentState.selectedServiceIds;
      delete this.bookingService.selectedBookingData.currentSelectedAppointmentState.selectedWorkerId;
      delete this.bookingService.selectedBookingData.currentSelectedAppointmentState.selectedWorker;
      delete this.bookingService.selectedBookingData.currentSelectedAppointmentState.selectedDate;
    }

    const selectedMeetingTypeId = this.bookingService?.selectedBookingData?.currentSelectedAppointmentState?.selectedMeetingTypeId;
    this.bookingService.selectedBookingData.currentSelectedAppointmentState = this.bookingService.resetCurrentSelectedAppointmentStateonStoreSelection();
    this.bookingService.selectedBookingData.currentSelectedAppointmentState.selectedStoreId = this.selectedStore?.id;
    this.bookingService.selectedBookingData.currentSelectedAppointmentState.selectedStore = this.selectedStore;
    this.bookingService.selectedBookingData.currentSelectedAppointmentState.selectedMeetingTypeId = selectedMeetingTypeId;


    this.bookingService.setAppointmentStateInLocalStorage(
      this.bookingService.selectedBookingData.currentSelectedAppointmentState
    );
    this.gotoPage.emit({ view: BOOKING_PAGE_VIEWS.SERVICES_VIEW, isUserInteracted });
  }

  setupTemplates(): void {
    this.storeLabelTemplate = this.bookingService.widgetTemplates.find(template => (template.identifier === WIDGET_TEMPLATES_CONST.WIDGET_APPOINTMENT_STORE_LABEL));
    this.storeLabelTemplate && (this.storeLabelTemplate.is_multi_language = 1);
    this.anyStoreLabelTemplate = this.bookingService.widgetTemplates.find(template => template.identifier === WIDGET_TEMPLATES_CONST.WIDGET_ANY_STORES);
    this.anyStoreLabelTemplate && (this.anyStoreLabelTemplate.is_multi_language = 1);
  }

  allowOnlyNumbers(event: KeyboardEvent): boolean {
    const numReg = new RegExp('^[0-9]$');
    if (numReg.test(event.key)) {
      return true;
    }
    // Only ASCII charactar in that range allowed
    const ASCIICode = event.which ? event.which : event.keyCode;

    if (ASCIICode === 8) {
      return true;
    }

    if (ASCIICode === 65 && event.metaKey) {
      return true;
    }

    return false;
  }
}
